import Typography from "antd/lib/typography";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
    type IClassroomUser,
    type IFrameworkResource,
    type IFrameworkSkill,
    type IUser,
    type IValidationResource,
    useFrameworkQuery,
} from "@sol/sdk";
import { Collapse, type CollapseProps } from "@sol/uikit/data-display/Collapse/Collapse";
import { Empty } from "@sol/uikit/data-display/Empty/Empty";
import { Spin } from "@sol/uikit/feedback/Spin/Spin";

import { SkillEvaluation, type SkillLevelItem } from "@/entities/skill";

import { getProgressBarLevels } from "./lib";

type FrameworkSkillsValidationsProps = {
    className?: string;
    framework: IFrameworkResource;
    validations: IValidationResource[];
    isInCard?: boolean;
    learner: IClassroomUser | IUser;
};

export const FrameworkSkillsValidations = ({
    className,
    framework,
    validations,
    isInCard,
    learner,
}: FrameworkSkillsValidationsProps) => {
    const [getDetails, setGetDetails] = useState(false);
    const [t] = useTranslation();

    const { title } = framework;

    const { data: frameworkDetails, isLoading } = useFrameworkQuery({ framework, learner }, { enabled: getDetails });

    const renderSkillEvaluation = (skill: IFrameworkSkill) => {
        const selfEvaluatuedLevels = skill.selfEvaluatedLevels || [0];
        const skillLevels = [skill.skillLevel1, skill.skillLevel2, skill.skillLevel3];
        const highestSelfEvaluatedSkillLevel = Math.max(...selfEvaluatuedLevels);

        const levels = getProgressBarLevels(skillLevels, validations, t);
        const selfAssessment: SkillLevelItem[] = skillLevels.map((level, index) => ({
            id: level,
            status: highestSelfEvaluatedSkillLevel > index ? "success" : "default",
            title: `${t("component.SkillLevel.level")} ${index + 1}`,
        }));

        return (
            <SkillEvaluation
                key={skill["@id"]}
                title={`${skill.code} - ${skill.shortTitle}`}
                assessment={levels}
                selfAssessment={selfAssessment}
                data-cy="skill-evaluation"
            />
        );
    };

    const items: CollapseProps["items"] = [
        {
            key: framework["@id"],
            classNames: { header: "!p-5" },
            label: <Typography.Text className="font-ant-strong">{title}</Typography.Text>,
            children: (
                <Spin spinning={isLoading}>
                    <div className="flex flex-wrap p-6">
                        <div
                            className={clsx("w-full", {
                                "grid grid-cols-1 gap-6": isInCard,
                                "grid grid-cols-2 gap-x-7 gap-y-9": !isInCard,
                            })}
                        >
                            {frameworkDetails && frameworkDetails.skills.length > 0 ? (
                                frameworkDetails.skills.map(renderSkillEvaluation)
                            ) : (
                                <Empty className="col-span-full" description={null} />
                            )}
                        </div>
                    </div>
                </Spin>
            ),
        },
    ];

    return (
        <li data-cy="framework-skills-validations">
            <Collapse
                className={className}
                items={items}
                expandIconPosition="end"
                onChange={() => setGetDetails(true)}
                destroyOnHidden
            />
        </li>
    );
};
